import React, { Suspense, lazy } from "react";
import { useSelector } from "react-redux";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ErrorBoundary from "./components/common/ErrorBoundary";
import DashboardLayout from "./components/layouts/DashboardLayout";
import FeatureGuard from "./components/common/FeatureGuard";
import {
  selectIsAuthenticated,
  selectCurrentUser,
} from "./store/slices/authSlice";

// Auth pages
const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const RegisterPage = lazy(() => import("./pages/auth/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("./pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/auth/ResetPasswordPage"));
const PendingApprovalPage = lazy(() => import("./pages/auth/PendingApprovalPage"));
const OnboardingPage = lazy(() => import("./pages/auth/OnboardingPage"));

// Dashboard pages
const DashboardHomePage = lazy(() => import("./pages/dashboard/DashboardHomePage"));
const FeedPage = lazy(() => import("./pages/dashboard/FeedPage"));
const EventsPage = lazy(() => import("./pages/dashboard/EventsPage"));
const GrievancesPage = lazy(() => import("./pages/dashboard/GrievancesPage"));
const TasksPage = lazy(() => import("./pages/dashboard/TasksPage"));
const HallReservationsPage = lazy(() => import("./pages/dashboard/HallReservationsPage"));
const CommunicationPage = lazy(() => import("./pages/dashboard/CommunicationPage"));
const DirectoryPage = lazy(() => import("./pages/dashboard/DirectoryPage"));
const DocumentsPage = lazy(() => import("./pages/dashboard/DocumentsPage"));
const ProfilePage = lazy(() => import("./pages/dashboard/ProfilePage"));
const LeadershipPage = lazy(() => import("./pages/dashboard/LeadershipPage"));
const MemberResourcesPage = lazy(() => import("./pages/dashboard/MemberResourcesPage"));
const EboardUpdatesPage = lazy(() => import("./pages/dashboard/EboardUpdatesPage"));
const ForumPage = lazy(() => import("./pages/dashboard/ForumPage"));
const OfficialsPage = lazy(() => import("./pages/dashboard/OfficialsPage"));
const VotingPage = lazy(() => import("./pages/dashboard/VotingPage"));
const RepFinderPage = lazy(() => import("./pages/dashboard/RepFinderPage"));
const StorePage = lazy(() => import("./pages/dashboard/StorePage"));
const SuggestionBoxPage = lazy(() => import("./pages/dashboard/SuggestionBoxPage"));
const PeerSupportPage = lazy(() => import("./pages/dashboard/PeerSupportPage"));
const ContractsPage = lazy(() => import("./pages/dashboard/ContractsPage"));

// Admin pages (heaviest — analytics, charts, management tables)
const AnalyticsDashboard = lazy(() => import("./pages/dashboard/admin/AnalyticsDashboard"));
const AdminPage = lazy(() => import("./pages/dashboard/admin/AdminPage"));
const FederationAdminPage = lazy(() => import("./pages/dashboard/FederationAdminPage"));

// Misc pages
const PlaceholderPage = lazy(() => import("./pages/dashboard/PlaceholderPage"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/legal/TermsOfService"));
const AccountDeletion = lazy(() => import("./pages/legal/AccountDeletion"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const FeatureDisabledPage = lazy(() => import("./pages/FeatureDisabledPage"));

const PageLoader = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
    <div style={{ width: 40, height: 40, borderRadius: "50%", border: "3px solid #e5e7eb", borderTopColor: "#3b82f6", animation: "spin 0.7s linear infinite" }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

function App() {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectCurrentUser);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/register/:inviteToken" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/pending-approval" element={<PendingApprovalPage />} />

            {/* Dashboard Routes with Layout */}
            <Route
              path="/dashboard"
              element={
                isAuthenticated ? (
                  user?.onboardingCompleted ? (
                    <DashboardLayout />
                  ) : (
                    <Navigate to="/onboarding" replace />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            >
              {/* Nested dashboard routes */}
              <Route index element={<DashboardHomePage />} />
              <Route
                path="feed"
                element={
                  <FeatureGuard feature="newsFeed">
                    <FeedPage />
                  </FeatureGuard>
                }
              />
              <Route
                path="events"
                element={
                  <FeatureGuard feature="events">
                    <EventsPage />
                  </FeatureGuard>
                }
              />
              <Route
                path="grievances"
                element={
                  <FeatureGuard feature="grievances">
                    <GrievancesPage />
                  </FeatureGuard>
                }
              />
              <Route
                path="rep-finder"
                element={
                  <FeatureGuard feature="repFinder">
                    <RepFinderPage />
                  </FeatureGuard>
                }
              />
              <Route
                path="tasks"
                element={
                  <FeatureGuard feature="tasks">
                    <TasksPage />
                  </FeatureGuard>
                }
              />
              <Route
                path="documents"
                element={
                  <FeatureGuard feature="documents">
                    <DocumentsPage />
                  </FeatureGuard>
                }
              />
              <Route
                path="hall"
                element={
                  <FeatureGuard feature="hallReservations">
                    <HallReservationsPage />
                  </FeatureGuard>
                }
              />
              <Route
                path="directory"
                element={
                  <FeatureGuard feature="memberDirectory">
                    <DirectoryPage />
                  </FeatureGuard>
                }
              />
              <Route
                path="leadership"
                element={
                  <FeatureGuard feature="leadership">
                    <LeadershipPage />
                  </FeatureGuard>
                }
              />
              <Route path="resources" element={<MemberResourcesPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="analytics" element={<AnalyticsDashboard />} />
              <Route
                path="eboard-updates"
                element={
                  <FeatureGuard feature="eboardUpdates">
                    <EboardUpdatesPage />
                  </FeatureGuard>
                }
              />

              <Route
                path="forum"
                element={
                  <FeatureGuard feature="forum">
                    <ForumPage />
                  </FeatureGuard>
                }
              />

              <Route
                path="suggestion-box"
                element={
                  <FeatureGuard feature="suggestionBox">
                    <SuggestionBoxPage />
                  </FeatureGuard>
                }
              />

              {/* Political Intelligence */}
              <Route
                path="officials"
                element={
                  <FeatureGuard feature="lobbying">
                    <OfficialsPage />
                  </FeatureGuard>
                }
              />

              <Route
                path="voting"
                element={
                  <FeatureGuard feature="voting">
                    <VotingPage />
                  </FeatureGuard>
                }
              />

              {/* Store */}
              <Route
                path="store"
                element={
                  <FeatureGuard feature="store">
                    <StorePage />
                  </FeatureGuard>
                }
              />

              {/* Peer Support */}
              <Route
                path="peer-support"
                element={
                  <FeatureGuard feature="peerSupport">
                    <PeerSupportPage />
                  </FeatureGuard>
                }
              />

              {/* Contracts */}
              <Route
                path="contracts"
                element={
                  <FeatureGuard feature="contracts">
                    <ContractsPage />
                  </FeatureGuard>
                }
              />

              {/* Communication Route - Unified page with tabs */}
              <Route path="communicate" element={<CommunicationPage />} />

              {/* Admin Route - Now with internal tab navigation */}
              <Route path="admin" element={<AdminPage />} />

              {/* Federation Admin Route */}
              <Route path="federation-admin" element={<FederationAdminPage />} />
            </Route>

            {/* Onboarding Route - Protected (requires authentication) */}
            <Route
              path="/onboarding"
              element={
                isAuthenticated ? (
                  <OnboardingPage />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* Legal Pages */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/account-deletion" element={<AccountDeletion />} />

            {/* Feature Disabled Page */}
            <Route path="/feature-disabled" element={<FeatureDisabledPage />} />

            {/* Default redirect */}
            <Route
              path="/"
              element={
                isAuthenticated ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* Catch-all for 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
